import { Lightbulb, MessageSquareText, Shield, ShieldCheck } from "lucide-react";
import { COURSE_TRAILER } from "@/config/course";
import { AI_SAFETY_LESSONS } from "./learning/aiSafety";
import { AI_SAFETY_EXPANDED } from "./learning/aiSafetyExpanded";
import { PROMPTING_LESSONS } from "./learning/prompting";
import { PROMPTING_EXPANDED } from "./learning/promptingExpanded";
import { CYBERSECURITY_LESSONS } from "./learning/cybersecurity";
import { CYBERSECURITY_EXPANDED } from "./learning/cybersecurityExpanded";
import { AI_FLUENCY_LESSONS } from "./learning/aiFluency";
import { AI_FLUENCY_EXPANDED } from "./learning/aiFluencyExpanded";
import { AI_SAFETY_ADVANCED, AI_FLUENCY_ADVANCED, CYBERSECURITY_ADVANCED, PROMPTING_ADVANCED } from "./learning/advanced";
import { AI_SAFETY_EVERYDAY, AI_FLUENCY_EVERYDAY, CYBERSECURITY_EVERYDAY, PROMPTING_EVERYDAY } from "./learning/everyday";
import type { LearningLesson, LearningTrack, LearningTrackId, LearningVideo } from "./learning/types";

export * from "./learning/types";

export const LEARNING_TRACKS: LearningTrack[] = [
  {
    id: "ai-safety",
    name: "AI Safety",
    icon: ShieldCheck,
    accentVar: "--track-safety",
    purpose: "Use AI responsibly and privately.",
    description:
      "Learn how to use AI responsibly, protect sensitive information, verify important answers, and recognize AI limitations.",
    lessons: [...AI_SAFETY_LESSONS, ...AI_SAFETY_EXPANDED, ...AI_SAFETY_EVERYDAY, ...AI_SAFETY_ADVANCED],
  },
  {
    id: "prompting",
    name: "Prompting",
    icon: MessageSquareText,
    accentVar: "--track-prompting",
    purpose: "Communicate clearly with AI.",
    description: "Learn how to give AI clear instructions, useful context, constraints, and output requirements.",
    lessons: [...PROMPTING_LESSONS, ...PROMPTING_EXPANDED, ...PROMPTING_EVERYDAY, ...PROMPTING_ADVANCED],
  },
  {
    id: "cybersecurity",
    name: "Cybersecurity",
    icon: Shield,
    accentVar: "--track-cyber",
    purpose: "Spot scams and protect your accounts.",
    description:
      "Build practical awareness around phishing, vishing, smishing, social engineering, account security, and AI-era threats.",
    lessons: [...CYBERSECURITY_LESSONS, ...CYBERSECURITY_EXPANDED, ...CYBERSECURITY_EVERYDAY, ...CYBERSECURITY_ADVANCED],
  },
  {
    id: "ai-fluency",
    name: "AI Fluency",
    icon: Lightbulb,
    accentVar: "--track-fluency",
    purpose: "Understand what AI can and can't do.",
    description:
      "Understand models, agents, tools, hallucinations, context, automation, and when humans should stay in control.",
    lessons: [...AI_FLUENCY_LESSONS, ...AI_FLUENCY_EXPANDED, ...AI_FLUENCY_EVERYDAY, ...AI_FLUENCY_ADVANCED],
  },
];

export const ALL_LESSONS: LearningLesson[] = LEARNING_TRACKS.flatMap((t) => t.lessons);

export const getTrack = (id?: string) => LEARNING_TRACKS.find((t) => t.id === id);
export const getLesson = (trackId?: string, slug?: string) =>
  getTrack(trackId)?.lessons.find((l) => l.slug === slug);
export const getLessonById = (id: string) => ALL_LESSONS.find((l) => l.id === id);
export const lessonPath = (l: Pick<LearningLesson, "trackId" | "slug">) => `/learn/${l.trackId}/${l.slug}`;

export const nextLessonAfter = (l: LearningLesson): LearningLesson | undefined => {
  const lessons = getTrack(l.trackId)!.lessons;
  return lessons[lessons.indexOf(l) + 1];
};

/** The real, existing AI Kenny video used for the hub spotlight. */
export const SPOTLIGHT_VIDEO: LearningVideo = {
  videoUrl: COURSE_TRAILER.videoUrl,
  posterUrl: COURSE_TRAILER.posterUrl,
  captionsUrl: COURSE_TRAILER.captionsUrl,
  durationLabel: COURSE_TRAILER.durationLabel,
};

export type { LearningTrackId };
